// hypercomb-shared/ui/swarm-adopt-panel/swarm-adopt-panel.component.ts
//
// Participant-grouped adoption panel for swarm tiles.
//
// Opens on the 'swarm:adopt-panel:open' effect (fired by SwarmAdoptDrone
// when the user clicks adopt on a peer tile). Shows everything every
// present participant is publishing at the current location, grouped by
// participant — overlapping names appear once per publisher, because two
// peers sharing the same name are two different adoptable branches even
// though only the top one renders on the canvas.
//
// Selection is per (pubkey, name). Per-participant select-all + a global
// confirm let the user adopt en masse; confirm emits
//   tile:action { action: 'adopt-selected', selections: [{label, pubkey}] }
// which SwarmAdoptDrone walks sequentially through the canonical
// portal:open handoff. The panel never fetches or commits anything.

import { Component, computed, signal, type OnDestroy, type OnInit } from '@angular/core'
import { EffectBus } from '@hypercomb/core'
import { TranslatePipe } from '../../core/i18n.pipe'

const SWARM_DRONE_KEY = '@diamondcoreprocessor.com/SwarmDrone'

interface SwarmDroneLike {
  peerTilesGroupedAtCurrentSig?: () => readonly {
    pubkey: string
    label: string
    tiles: readonly ({ name: string } & Record<string, unknown>)[]
  }[]
}

/** Deterministic per-pubkey color (DJB2 → HSL → RGB). Same algorithm as
 *  layer-cycle-strip + show-cell label tints, so a participant's color is
 *  consistent across the canvas, the strip, and this panel. */
function pubkeyColor(pubkey: string): string {
  if (!pubkey) return 'rgb(160, 160, 160)'
  let hash = 5381
  for (let i = 0; i < pubkey.length; i++) hash = ((hash << 5) + hash + pubkey.charCodeAt(i)) | 0
  hash = hash >>> 0
  const hue = (hash % 360) / 360
  const sat = 0.65
  const lit = 0.6
  const c = (1 - Math.abs(2 * lit - 1)) * sat
  const x = c * (1 - Math.abs(((hue * 6) % 2) - 1))
  const m = lit - c / 2
  let r = 0, g = 0, b = 0
  const sector = (hue * 6) | 0
  if (sector === 0)      { r = c; g = x; b = 0 }
  else if (sector === 1) { r = x; g = c; b = 0 }
  else if (sector === 2) { r = 0; g = c; b = x }
  else if (sector === 3) { r = 0; g = x; b = c }
  else if (sector === 4) { r = x; g = 0; b = c }
  else                   { r = c; g = 0; b = x }
  const R = Math.round((r + m) * 255)
  const G = Math.round((g + m) * 255)
  const B = Math.round((b + m) * 255)
  return `rgb(${R}, ${G}, ${B})`
}

interface TileRow {
  readonly name: string
  /** Selection key — `${pubkey} ${name}`. */
  readonly key: string
  /** Adoptable = the visual carries a valid layerSig the installer can
   *  resolve. Tiles without one render but can't be adopted (yet). */
  readonly adoptable: boolean
}

interface ParticipantGroup {
  readonly pubkey: string
  readonly display: string
  readonly color: string
  readonly tiles: readonly TileRow[]
}

const SIG_RE = /^[a-f0-9]{64}$/

@Component({
  selector: 'hc-swarm-adopt-panel',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './swarm-adopt-panel.component.html',
  styleUrls: ['./swarm-adopt-panel.component.scss'],
})
export class SwarmAdoptPanelComponent implements OnInit, OnDestroy {

  readonly open = signal(false)
  readonly groups = signal<readonly ParticipantGroup[]>([])
  readonly #selected = signal<ReadonlySet<string>>(new Set())

  readonly selectedCount = computed(() => this.#selected().size)

  #openUnsub: (() => void) | null = null
  #peersUnsub: (() => void) | null = null
  #escapeUnsub: (() => void) | null = null

  ngOnInit(): void {
    this.#openUnsub = EffectBus.on<{ preselect?: readonly string[] }>(
      'swarm:adopt-panel:open',
      (payload) => this.#openPanel(payload?.preselect ?? []),
    )

    // Live swarm — participants join/leave/republish while the panel is
    // up. Re-snapshot on every peers-changed so the groups stay truthful;
    // selections survive by key (a pick from a departed peer just drops).
    this.#peersUnsub = EffectBus.on('swarm:peers-changed', () => {
      if (this.open()) this.#refresh()
    })

    this.#escapeUnsub = EffectBus.on<{ cmd: string }>('keymap:invoke', (payload) => {
      if (payload?.cmd === 'global.escape' && this.open()) this.dismiss()
    })
  }

  ngOnDestroy(): void {
    this.#openUnsub?.()
    this.#peersUnsub?.()
    this.#escapeUnsub?.()
  }

  isSelected(key: string): boolean {
    return this.#selected().has(key)
  }

  toggleTile(tile: TileRow): void {
    if (!tile.adoptable) return
    const next = new Set(this.#selected())
    if (next.has(tile.key)) next.delete(tile.key)
    else next.add(tile.key)
    this.#selected.set(next)
  }

  groupAllSelected(group: ParticipantGroup): boolean {
    const adoptable = group.tiles.filter(t => t.adoptable)
    return adoptable.length > 0 && adoptable.every(t => this.#selected().has(t.key))
  }

  toggleGroup(group: ParticipantGroup): void {
    const adoptable = group.tiles.filter(t => t.adoptable)
    if (adoptable.length === 0) return
    const next = new Set(this.#selected())
    if (this.groupAllSelected(group)) {
      for (const t of adoptable) next.delete(t.key)
    } else {
      for (const t of adoptable) next.add(t.key)
    }
    this.#selected.set(next)
  }

  adoptSelected(): void {
    const picked = this.#selected()
    if (picked.size === 0) return
    // Walk groups in display order so adoption fires top-publisher-first,
    // matching what the user read on screen.
    const selections: { label: string; pubkey: string }[] = []
    for (const group of this.groups()) {
      for (const tile of group.tiles) {
        if (picked.has(tile.key)) selections.push({ label: tile.name, pubkey: group.pubkey })
      }
    }
    this.open.set(false)
    this.#selected.set(new Set())
    EffectBus.emit('tile:action', { action: 'adopt-selected', selections })
  }

  dismiss(): void {
    this.open.set(false)
    this.#selected.set(new Set())
  }

  #openPanel(preselect: readonly string[]): void {
    this.#refresh()
    // Preselect each requested name in the FIRST group that carries it —
    // groups are freshness-first, so that's the publisher whose copy is
    // currently rendering on the canvas (the "top tile").
    const wanted = new Set(preselect.map(s => String(s ?? '').trim()).filter(Boolean))
    if (wanted.size > 0) {
      const next = new Set(this.#selected())
      for (const name of wanted) {
        for (const group of this.groups()) {
          const tile = group.tiles.find(t => t.name === name && t.adoptable)
          if (tile) { next.add(tile.key); break }
        }
      }
      this.#selected.set(next)
    }
    this.open.set(true)
  }

  #refresh(): void {
    const swarm = (window as { ioc?: { get?: (k: string) => unknown } }).ioc?.get?.(
      SWARM_DRONE_KEY,
    ) as SwarmDroneLike | undefined
    const raw = swarm?.peerTilesGroupedAtCurrentSig?.() ?? []
    const groups: ParticipantGroup[] = raw.map(g => ({
      pubkey: g.pubkey,
      display: g.label || g.pubkey.slice(0, 8),
      color: pubkeyColor(g.pubkey),
      tiles: g.tiles.map(t => ({
        name: t.name,
        key: `${g.pubkey} ${t.name}`,
        adoptable: SIG_RE.test(String(t['layerSig'] ?? '')),
      })),
    })).filter(g => g.tiles.length > 0)
    this.groups.set(groups)
    // Prune selections whose tile vanished (peer left / republished
    // without that child).
    const live = new Set(groups.flatMap(g => g.tiles.map(t => t.key)))
    const pruned = new Set([...this.#selected()].filter(k => live.has(k)))
    if (pruned.size !== this.#selected().size) this.#selected.set(pruned)
  }
}
